/**
 * L1-FE-02 — Upload record detail.
 *
 * Permalink for a single stored upload (GET /uploads/{id} → ManualUpload). The
 * read endpoint returns metadata + lifecycle status only (no row-level
 * validation — those errors are on the ingest response shown in the portal at
 * upload time). This page shows the record, lets a reviewer open the parsed
 * rows (GET /uploads/{id}/rows), and — when the upload is parse-clean
 * (`parsed`) — offers the Accept-into-batch action (POST /uploads/{id}/accept).
 *
 * State-branched on the record's status:
 *   - received   → "wasn't parsed cleanly" alert + fix-and-reupload CTA. The
 *                  backend parses inline on POST, so a *persisted* `received`
 *                  is a parse failure (not an in-flight scan) and never
 *                  auto-resolves — we don't poll it.
 *   - rejected   → "rejected" alert + fix-and-reupload CTA
 *   - parsed     → "ready to accept" + Accept-into-batch action
 *   - accepted   → green "applied to dataset" note
 *   - superseded → "replaced by a newer upload" pointer
 *
 * NOTE: the stored record carries no row-level parse errors — those are
 * returned only by the POST ingest response and rendered inline in the
 * upload portal (Uploads.tsx). When the BE enriches ManualUploadRecord
 * with a row-error summary (see the BE follow-up), restore the table here.
 *
 * Fix-and-reupload path: a button that navigates back to /uploads where the
 * same type's Dropzone is ready for the corrected file.
 */

import { isApiError } from "@compliance/domain";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconCircleCheckFilled,
  IconCloudUpload,
  IconCopyCheck,
  IconFileImport,
  IconReload,
  IconShieldX,
  IconTable,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAcceptUpload, useBatches, useUpload } from "../api/hooks";
import type { ManualUpload } from "../api/types";
import { useHasRole } from "../auth";
import { useUploadTypeLabels } from "../auth/useBranding";
import { UploadRowsDrawer } from "../components/data/UploadRowsDrawer";
import { canAcceptUpload } from "../lib/batchState";
import { formatPacificDisplay } from "../lib/datetime";
import { invalidateBatchData, refreshInBackground } from "../lib/invalidate";
import { queryKeys } from "../lib/queryKeys";
import { UPLOAD_STATUS_COLOR } from "../lib/uploadTypes";

export function UploadDetailPage() {
  const typeLabels = useUploadTypeLabels();
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const uploadQ = useUpload(uploadId);
  // A persisted `received` is a parse failure, not an in-flight scan (the BE
  // parses inline on POST), so there is no terminal state to poll toward.
  const [rowsOpen, setRowsOpen] = useState(false);

  if (uploadQ.isPending) {
    return (
      <Stack gap="lg">
        <Skeleton height={14} width={140} />
        <Skeleton height={36} width={420} />
        <Skeleton height={18} width={520} />
        <Skeleton height={280} radius="md" mt="md" />
      </Stack>
    );
  }

  // The hook swallows 404 (and the "no upload with that id" 400) from
  // GET /uploads/{id} into a `null` result, so not-found never arrives as an
  // error — a null `data` is the only not-found signal.
  const notFound = uploadQ.data === null;
  if (uploadQ.isError || uploadQ.data === null) {
    return (
      <Stack gap="md">
        <Anchor component={Link} to="/uploads" size="sm" c="dimmed">
          ← Back to uploads
        </Anchor>
        <Title order={3}>{notFound ? "Upload not found" : "Couldn't load upload"}</Title>
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">
            {notFound ? (
              <>
                No upload with ID{" "}
                <Text span ff="monospace">
                  {uploadId}
                </Text>
                .
              </>
            ) : (
              (uploadQ.error as Error).message
            )}
          </Text>
        </Alert>
      </Stack>
    );
  }

  const upload = uploadQ.data;
  // `superseded` uploads are retained for the audit trail, so their parsed rows
  // + row hashes must stay inspectable from Upload history.
  const hasRows =
    upload.status === "parsed" || upload.status === "accepted" || upload.status === "superseded";

  return (
    <Stack gap="lg">
      <Stack gap={6}>
        <Anchor component={Link} to="/uploads" size="sm" c="dimmed">
          ← Back to uploads
        </Anchor>
        <Group gap="md" align="center" wrap="wrap">
          <ThemeIcon variant="light" color="indigo" radius="md" size={36}>
            <IconCloudUpload size={20} />
          </ThemeIcon>
          <Title order={2} ff="monospace" fz={{ base: "h3", sm: "h2" }}>
            {upload.original_filename}
          </Title>
          <Badge color={UPLOAD_STATUS_COLOR[upload.status]} variant="light" size="md">
            {upload.status}
          </Badge>
        </Group>
        <Group gap="sm" wrap="wrap">
          <Text size="sm" c="dimmed">
            {typeLabels[upload.upload_type]}
          </Text>
          <Text size="sm" c="dimmed">
            ·
          </Text>
          <Text size="sm" c="dimmed">
            period {upload.period}
          </Text>
          <Text size="sm" c="dimmed">
            ·
          </Text>
          <Text size="sm" c="dimmed" ff="monospace">
            {upload.batch_id ?? "not linked to a batch"}
          </Text>
          <Text size="sm" c="dimmed">
            ·
          </Text>
          <Text size="sm" c="dimmed">
            uploaded {formatPacificDisplay(upload.uploaded_at)} by {upload.uploaded_by}
          </Text>
        </Group>
        <Group gap="sm" wrap="wrap" mt={2}>
          <Text size="xs" c="dimmed" ff="monospace">
            sha256 {upload.original_sha256.slice(0, 12)}…
          </Text>
          <Text size="xs" c="dimmed">
            ·
          </Text>
          <Text size="xs" c="dimmed">
            {(upload.content_length / 1024).toFixed(1)} KB
          </Text>
          {upload.row_count != null ? (
            <>
              <Text size="xs" c="dimmed">
                ·
              </Text>
              <Text size="xs" c="dimmed">
                {upload.row_count} row{upload.row_count === 1 ? "" : "s"}
              </Text>
            </>
          ) : null}
          {hasRows ? (
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconTable size={14} />}
              onClick={() => setRowsOpen(true)}
            >
              View parsed rows
            </Button>
          ) : null}
        </Group>
      </Stack>

      {/* A persisted `received` is a parse failure (BE parses inline on POST),
          so it shares the fix-and-reupload path with `rejected` rather than
          showing an in-flight spinner. Only a parse-clean `parsed` upload can
          be accepted into a batch. */}
      {upload.status === "rejected" || upload.status === "received" ? (
        <NeedsReuploadView status={upload.status} onFixAndReupload={() => navigate("/uploads")} />
      ) : upload.status === "superseded" ? (
        <SupersededView upload={upload} />
      ) : upload.status === "accepted" ? (
        <AcceptedView upload={upload} />
      ) : (
        <AcceptPanel upload={upload} />
      )}

      <UploadRowsDrawer
        uploadId={hasRows ? upload.id : null}
        title={upload.original_filename}
        opened={rowsOpen}
        onClose={() => setRowsOpen(false)}
      />
    </Stack>
  );
}

// ── State-branched bodies ──────────────────────────────────────────────────

/**
 * Fix-and-reupload state, shared by `rejected` and `received`. The stored
 * record carries no row-level errors (GET /uploads/{id} omits them), so both
 * point the operator back to the portal — where the per-row errors were shown
 * at upload time — to re-upload a corrected file. A `received` record reaches
 * here because the inline parse left it un-advanced (row errors), not because
 * a scan is still running.
 */
function NeedsReuploadView({
  status,
  onFixAndReupload,
}: {
  status: "rejected" | "received";
  onFixAndReupload: () => void;
}) {
  const title = status === "rejected" ? "This upload was rejected" : "This upload wasn't parsed";
  return (
    <Alert color="red" variant="light" icon={<IconShieldX size={16} />} title={title}>
      <Stack gap="xs">
        <Text size="sm">
          Row-level errors blocked this upload — none of its rows were applied to the batch dataset.
          The per-row error list is shown in the upload portal at upload time. Fix the issues in the
          source file and re-upload — the corrected file replaces this one.
        </Text>
        <Group gap="sm">
          <Button
            size="xs"
            color="indigo"
            leftSection={<IconReload size={14} />}
            onClick={onFixAndReupload}
          >
            Fix and re-upload
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
}

function AcceptedView({ upload }: { upload: ManualUpload }) {
  const acceptedMsg =
    upload.row_count != null
      ? `${upload.row_count} row${upload.row_count === 1 ? "" : "s"} accepted into the batch dataset.`
      : "Accepted into the batch dataset.";
  return (
    <Alert
      color="teal"
      variant="light"
      icon={<IconCircleCheckFilled size={18} />}
      title="Applied to the batch dataset"
    >
      <Text size="sm">{acceptedMsg}</Text>
    </Alert>
  );
}

function SupersededView({ upload }: { upload: ManualUpload }) {
  return (
    <Alert
      color="yellow"
      variant="light"
      icon={<IconCopyCheck size={16} />}
      title="Replaced by a newer upload"
    >
      <Text size="sm">
        A newer upload of this type for period {upload.period} has superseded this record. It's
        retained for the audit trail but no longer feeds the dataset — the active upload is the most
        recent non-superseded one for the type.
      </Text>
    </Alert>
  );
}

/**
 * Accept-into-batch (L1-BE-07). A parse-clean upload isn't part of the dataset
 * until accepted: POST /uploads/{id}/accept links it to a batch, bumps
 * data_version, and re-runs the RFS calc. The upload may not yet be linked to a
 * batch (`batch_id` null), so the reviewer picks the target. Auditor-forbidden
 * server-side; reason required.
 */
function AcceptPanel({ upload }: { upload: ManualUpload }) {
  const canAccept = useHasRole("operations", "reviewer", "admin");
  const accept = useAcceptUpload();
  const queryClient = useQueryClient();
  const batchesQ = useBatches();
  const [opened, setOpened] = useState(false);
  const [reason, setReason] = useState("");

  // An upload is period-scoped, so it can only be accepted into a batch for the
  // same reporting period that can still accept data — accepting into another
  // period breaks the scoping, and accepting into a locked (Approved/Submitted/
  // Frozen) batch is rejected server-side only after a reason is entered.
  // Restrict the options on both axes (see canAcceptUpload for the state rule).
  const batches = useMemo(() => batchesQ.data?.items ?? [], [batchesQ.data]);
  const periodBatches = useMemo(
    () =>
      batches.filter(
        (b) => b.period_start.slice(0, 7) === upload.period && canAcceptUpload(b.state),
      ),
    [batches, upload.period],
  );
  // Default to the upload's linked batch (if it's an eligible target), else the
  // Open batch for the period, else the first eligible batch.
  const defaultBatchId = useMemo(() => {
    if (upload.batch_id && periodBatches.some((b) => b.batch_id === upload.batch_id)) {
      return upload.batch_id;
    }
    return (
      periodBatches.find((b) => b.state === "Open")?.batch_id ?? periodBatches[0]?.batch_id ?? null
    );
  }, [periodBatches, upload.batch_id]);
  const [batchId, setBatchId] = useState<string | null>(null);
  const effectiveBatchId = batchId ?? defaultBatchId;

  const reasonValid = reason.trim().length >= 3;
  if (!canAccept) {
    return (
      <Alert
        color="blue"
        variant="light"
        icon={<IconFileImport size={16} />}
        title="Ready to accept"
      >
        <Text size="sm">Parsed cleanly. A reviewer can accept this upload into a batch.</Text>
      </Alert>
    );
  }

  const close = () => {
    if (accept.isPending) return;
    setReason("");
    accept.reset();
    setOpened(false);
  };

  const onConfirm = async () => {
    if (!effectiveBatchId) return;
    try {
      const result = await accept.mutateAsync({
        uploadId: upload.id,
        batchId: effectiveBatchId,
        reason: reason.trim(),
      });
      notifications.show({
        title: "Upload accepted",
        message: `Applied to ${result.batch_id} · data_version ${result.old_data_version} → ${result.new_data_version}.`,
        color: "teal",
        autoClose: 4000,
      });
      setReason("");
      accept.reset();
      setOpened(false);
      refreshInBackground(
        Promise.all([
          // Prefix invalidation — refreshes this detail row plus the portal +
          // history lists (queryKeys.uploads.list) and the cached rows, so the
          // accepted upload stops showing as parsed/unlinked everywhere.
          queryClient.invalidateQueries({ queryKey: queryKeys.uploads.all }),
          invalidateBatchData(queryClient, result.batch_id),
        ]),
      );
    } catch {
      // Surfaced inline in the modal.
    }
  };

  return (
    <>
      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              Ready to accept
            </Text>
            <Text size="xs" c="dimmed" maw={560}>
              Parsed cleanly. Accepting links these rows to a batch, bumps its data version, and
              recomputes the workpaper.
            </Text>
          </Stack>
          <Button
            color="teal"
            leftSection={<IconFileImport size={16} />}
            onClick={() => setOpened(true)}
            data-tour="upload-accept"
          >
            Accept into batch
          </Button>
        </Group>
      </Paper>

      <Modal opened={opened} onClose={close} title="Accept upload into batch" centered>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Accept{" "}
            <Text span ff="monospace">
              {upload.original_filename}
            </Text>{" "}
            — this bumps the batch&apos;s data version and recomputes the workpaper.
          </Text>
          {accept.error ? (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">
                {isApiError(accept.error)
                  ? `${accept.error.code}: ${accept.error.message}`
                  : (accept.error as Error).message}
              </Text>
            </Alert>
          ) : null}
          <Select
            label="Target batch"
            description={`Only unlocked batches for period ${upload.period} can accept this upload.`}
            placeholder={
              batchesQ.isPending
                ? "Loading…"
                : periodBatches.length === 0
                  ? `No unlocked batch for period ${upload.period}`
                  : "Select a batch"
            }
            data={periodBatches.map((b) => ({
              value: b.batch_id,
              label: `${b.period_label ?? b.period_start.slice(0, 7)} · ${b.state} · ${b.batch_id}`,
            }))}
            value={effectiveBatchId}
            onChange={setBatchId}
            disabled={batchesQ.isPending || periodBatches.length === 0}
            searchable
          />
          <Textarea
            label="Reason"
            description="Recorded in the audit log. Minimum 3 characters."
            minRows={3}
            autosize
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            error={
              reason.length > 0 && !reasonValid ? "Reason must be at least 3 characters" : null
            }
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={close} disabled={accept.isPending}>
              Cancel
            </Button>
            <Button
              color="teal"
              onClick={onConfirm}
              data-tour="upload-accept-confirm"
              disabled={!reasonValid || !effectiveBatchId || accept.isPending}
              loading={accept.isPending}
            >
              Accept
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
